import { createContext, useContext, useCallback, useEffect, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import {
  useGetProfileMe,
  useUpdateProfileMe,
  useRequestUploadUrl,
  getGetProfileMeQueryKey,
  type UserProfile,
  type UpdateProfileBody,
  type GetProfileMeQueryError,
} from "@workspace/api-client-react";

export type { UserProfile };

const MAX_AVATAR_SIZE = 2 * 1024 * 1024; // 2 MB

interface UserProfileContextValue {
  profile: UserProfile | null;
  isLoading: boolean;
  hasCompletedOnboarding: boolean;
  refreshProfile: () => Promise<void>;
  updateProfile: (username: string, avatarUrl?: string | null) => Promise<void>;
  uploadAvatar: (file: File) => Promise<string>;
}

const UserProfileContext = createContext<UserProfileContextValue | null>(null);

export function UserProfileProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  const profileQuery = useGetProfileMe({
    query: { enabled: !!currentUser, queryKey: getGetProfileMeQueryKey() },
  });

  const updateMutation = useUpdateProfileMe();
  const requestUrlMutation = useRequestUploadUrl();

  useEffect(() => {
    if (!currentUser) {
      queryClient.removeQueries({ queryKey: getGetProfileMeQueryKey() });
    }
  }, [currentUser, queryClient]);

  const uploadAvatar = useCallback(
    async (file: File): Promise<string> => {
      if (!currentUser) throw new Error("Not authenticated");
      if (file.size > MAX_AVATAR_SIZE) throw new Error("Image must be under 2 MB");
      if (!file.type.startsWith("image/")) throw new Error("File must be an image");

      const result = await requestUrlMutation.mutateAsync({
        data: { name: file.name, size: file.size, contentType: file.type },
      });

      const uploadRes = await fetch(result.uploadURL, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!uploadRes.ok) throw new Error("Failed to upload image");

      return result.objectPath;
    },
    [currentUser, requestUrlMutation],
  );

  const updateProfile = useCallback(
    async (username: string, avatarUrl?: string | null) => {
      if (!currentUser) throw new Error("Not authenticated");

      const body: UpdateProfileBody = { username };
      if (avatarUrl) body.avatarUrl = avatarUrl;

      const updated = await updateMutation.mutateAsync({ data: body });
      queryClient.setQueryData(getGetProfileMeQueryKey(), updated);
    },
    [currentUser, updateMutation, queryClient],
  );

  const refreshProfile = useCallback(async () => {
    await profileQuery.refetch();
  }, [profileQuery]);

  const profile = currentUser ? (profileQuery.data ?? null) : null;
  const isLoading = !!currentUser && profileQuery.isLoading;
  const hasCompletedOnboarding = profile !== null && profile.username !== null;

  return (
    <UserProfileContext.Provider
      value={{
        profile,
        isLoading,
        hasCompletedOnboarding,
        refreshProfile,
        updateProfile,
        uploadAvatar,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile(): UserProfileContextValue {
  const ctx = useContext(UserProfileContext);
  if (!ctx) throw new Error("useUserProfile must be used within <UserProfileProvider>");
  return ctx;
}
